import { useEffect, useRef } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';

type Props = {
  /** HTML inicial. */
  valor: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Quando true, mostra apenas o conteúdo sem toolbar nem edição. */
  somenteLeitura?: boolean;
  /** Limite máximo de caracteres do HTML (proteção do backend). */
  maxCaracteresHtml?: number;
};

export function RichTextEditor({
  valor,
  onChange,
  placeholder,
  somenteLeitura = false,
  maxCaracteresHtml,
}: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image,
    ],
    content: valor || '',
    editable: !somenteLeitura,
    editorProps: {
      attributes: {
        class: 'rte__conteudo',
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (maxCaracteresHtml && html.length > maxCaracteresHtml) return;
      onChange(html);
    },
  });

  // Sincroniza valor externo quando muda fora do editor (ex.: descartar).
  const ultimoValorRef = useRef(valor);
  useEffect(() => {
    if (!editor) return;
    if (valor === ultimoValorRef.current) return;
    ultimoValorRef.current = valor;
    if (editor.getHTML() !== valor) {
      editor.commands.setContent(valor || '', { emitUpdate: false });
    }
  }, [editor, valor]);

  if (!editor) {
    return <div className="rte rte--carregando">Carregando editor…</div>;
  }

  return (
    <div className={`rte${somenteLeitura ? ' rte--leitura' : ''}`}>
      {!somenteLeitura && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  function inserirLink() {
    const anterior = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL do link (deixe em branco para remover):', anterior ?? '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  function inserirImagemUrl() {
    const url = window.prompt('URL da imagem:');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  }

  function inserirImagemArquivo(file: File) {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result ?? '');
      if (!dataUrl.startsWith('data:image/')) return;
      editor.chain().focus().setImage({ src: dataUrl }).run();
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="rte__toolbar" role="toolbar" aria-label="Formatação do texto">
      <BotaoToolbar
        ativo={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        titulo="Negrito (Ctrl+B)"
      >
        <b>B</b>
      </BotaoToolbar>
      <BotaoToolbar
        ativo={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        titulo="Itálico (Ctrl+I)"
      >
        <i>I</i>
      </BotaoToolbar>
      <BotaoToolbar
        ativo={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        titulo="Sublinhado (Ctrl+U)"
      >
        <u>U</u>
      </BotaoToolbar>
      <BotaoToolbar
        ativo={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        titulo="Tachado"
      >
        <s>S</s>
      </BotaoToolbar>

      <Separador />

      <BotaoToolbar
        ativo={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        titulo="Título 1"
      >
        H1
      </BotaoToolbar>
      <BotaoToolbar
        ativo={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        titulo="Título 2"
      >
        H2
      </BotaoToolbar>
      <BotaoToolbar
        ativo={editor.isActive('paragraph')}
        onClick={() => editor.chain().focus().setParagraph().run()}
        titulo="Parágrafo"
      >
        ¶
      </BotaoToolbar>

      <Separador />

      <BotaoToolbar
        ativo={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        titulo="Lista com marcadores"
      >
        • —
      </BotaoToolbar>
      <BotaoToolbar
        ativo={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        titulo="Lista numerada"
      >
        1. —
      </BotaoToolbar>
      <BotaoToolbar
        ativo={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        titulo="Citação"
      >
        ❝
      </BotaoToolbar>
      <BotaoToolbar
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        titulo="Linha horizontal"
      >
        ―
      </BotaoToolbar>

      <Separador />

      <BotaoToolbar
        ativo={editor.isActive('link')}
        onClick={inserirLink}
        titulo="Inserir/editar link"
      >
        🔗
      </BotaoToolbar>
      <BotaoToolbar onClick={inserirImagemUrl} titulo="Inserir imagem por URL">
        🖼 URL
      </BotaoToolbar>
      <BotaoArquivoImagem onArquivo={inserirImagemArquivo} />

      <Separador />

      <BotaoToolbar
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        titulo="Limpar formatação"
      >
        ✕
      </BotaoToolbar>
      <BotaoToolbar
        onClick={() => editor.chain().focus().undo().run()}
        desabilitado={!editor.can().undo()}
        titulo="Desfazer (Ctrl+Z)"
      >
        ⟲
      </BotaoToolbar>
      <BotaoToolbar
        onClick={() => editor.chain().focus().redo().run()}
        desabilitado={!editor.can().redo()}
        titulo="Refazer (Ctrl+Y)"
      >
        ⟳
      </BotaoToolbar>
    </div>
  );
}

function BotaoToolbar({
  children,
  onClick,
  ativo,
  desabilitado,
  titulo,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ativo?: boolean;
  desabilitado?: boolean;
  titulo?: string;
}) {
  return (
    <button
      type="button"
      className={`rte__botao${ativo ? ' rte__botao--ativo' : ''}`}
      onClick={onClick}
      disabled={desabilitado}
      title={titulo}
      aria-label={titulo}
      aria-pressed={ativo}
    >
      {children}
    </button>
  );
}

function BotaoArquivoImagem({ onArquivo }: { onArquivo: (f: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        className="rte__botao"
        onClick={() => inputRef.current?.click()}
        title="Inserir imagem do computador"
        aria-label="Inserir imagem do computador"
      >
        🖼 Arquivo
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onArquivo(f);
          e.target.value = '';
        }}
      />
    </>
  );
}

function Separador() {
  return <span className="rte__separador" aria-hidden="true" />;
}
